import React from 'react';
import { HelpCircle, ChevronDown } from 'lucide-react';

/**
 * Themed accordion-style FAQ section.
 * Used on the main /faqs page and on every university detail page.
 * Uses <details>/<summary> for zero-dep, accessible, keyboard-friendly toggling.
 */
export default function FaqSection({
  eyebrow = 'FAQ',
  title,
  subtitle,
  faqs = [],
  variant = 'blue', // 'blue' | 'red'
  testid = 'faq-section',
}) {
  const accent = variant === 'red' ? 'bg-brand' : 'bg-primary';
  const accentText = variant === 'red' ? 'text-brand' : 'text-primary';

  return (
    <section className="py-14 md:py-16 bg-white" data-testid={testid}>
      <div className="container mx-auto max-w-5xl px-4 md:px-8">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold ${accentText}`}>
            <HelpCircle className="w-4 h-4" /> {eyebrow}
          </div>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary leading-tight">
            {title}
          </h2>
          <div className={`mx-auto mt-4 h-1 w-16 rounded-full ${accent}`} />
          {subtitle && (
            <p className="mt-5 text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>

        <div className="space-y-3">
          {faqs.map((item, i) => (
            <details
              key={i}
              className="group bg-white rounded-xl border border-border shadow-sm hover:shadow-md hover:border-brand/40 transition-all overflow-hidden"
              data-testid={`${testid}-item-${i}`}
            >
              <summary className="flex items-center justify-between gap-4 cursor-pointer list-none p-5 md:p-6 select-none">
                <div className="flex items-start gap-3">
                  <div className={`shrink-0 w-8 h-8 rounded-lg ${accent} text-white text-sm font-bold flex items-center justify-center mt-0.5`}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="font-serif text-base md:text-lg font-bold text-primary leading-snug">
                    {item.q}
                  </h3>
                </div>
                <div className={`shrink-0 w-9 h-9 rounded-full bg-muted flex items-center justify-center transition-transform duration-300 group-open:rotate-180 group-open:${accent} group-open:text-white`}>
                  <ChevronDown className="w-4 h-4" />
                </div>
              </summary>
              <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1">
                <div className="pl-11 text-sm md:text-base text-muted-foreground leading-relaxed">
                  {item.a}
                </div>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
